import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PersonIcon from "@mui/icons-material/Person";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { EventsTimeline } from "@shared/eventsTimelineComponent/EventsTimeline";
import type { TimelineEvent } from "@shared/eventsTimelineComponent/types";
import { RemindersListComponent } from "@shared/msoffice/products/demoShipmentFeed/components/RemindersListComponent";
import { OutlookPluginProvider } from "@shared/msoffice/context/OutlookPluginContext";
import type { OutlookPluginAdapter } from "@shared/msoffice/context/types";
import type { MsofficeApiClient, MsofficeApiFunctions } from "@shared/msoffice/api/types";
import DobbyChat from "@shared/msoffice/components/Chat/DobbyChatComponent";
import { ShipmentDetails } from "@shared/msoffice/products/demoShipmentFeed/components/ShipmentDetails";
import type { ChatSource } from "@services/chat/types";
import type { Reminder } from "@services/demo/msoffice/types";
import { apiClient } from "../../utils/apiClient";

// ── Types ──

export interface DrawerShipment {
  id: string;
  name: string;
  properties: Array<{ name: string; value: string }>;
}

interface DemoEmail {
  emailId: string;
  subject: string;
  from: string[];
  to: string[];
  date: string;
  body: string;
  snippet: string;
  attachments: Array<{ filename: string; attachmentId: string }>;
}

interface ShipmentDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  shipment: DrawerShipment | null;
  allReminders: Reminder[];
  defaultChatAnswer: { dobbyResponse: string; sources?: ChatSource[] } | null;
}

// ── Constants ──

const DRAWER_WIDTH = 620;

// ── Helpers ──

const getPropertyValue = (shipment: DrawerShipment, name: string): string => {
  const prop = shipment.properties.find((p) => p.name === name);
  return prop?.value || "";
};

const formatEmailDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const getInitials = (email: string): string => {
  const name = email.split("@")[0].split(/[._-]/);
  return name
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("");
};

// ── Mock providers ──

const mockAdapter: OutlookPluginAdapter = {
  openEmail: async () => {},
  getCurrentMessageId: () => null,
  getTargetMailbox: async () => null,
  openUrl: (url: string) => window.open(url, "_blank"),
  draftEmail: (to: string[], subject: string, body: string) => {
    const mailto = `mailto:${to.join(",")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailto, "_blank");
  },
  isContextReady: () => true,
  isDevMode: () => true,
};

const mockApiClient: MsofficeApiClient = {
  get: async () => ({} as any),
  post: async () => ({} as any),
};

const mockApi: MsofficeApiFunctions = {
  draftMessage: async () => ({ status: "ok" }),
  getEmailWithAttachments: async () => ({} as any),
};

// ── Component ──

export function ShipmentDetailDrawer({
  open,
  onClose,
  shipment,
  allReminders,
  defaultChatAnswer,
}: ShipmentDetailDrawerProps) {
  const [drawerTab, setDrawerTab] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [timelineOpen, setTimelineOpen] = useState(true);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [emailDialog, setEmailDialog] = useState<DemoEmail | null>(null);
  const [highlightedEmailId, setHighlightedEmailId] = useState<string | null>(null);

  // Reset tab and fetch timeline when shipment changes
  useEffect(() => {
    if (!shipment) return;
    setDrawerTab(0);
    setHighlightedEmailId(null);
    const fetchTimeline = async () => {
      try {
        setTimelineLoading(true);
        const response = await apiClient.get<{ events: TimelineEvent[] }>(
          `/demo-shipment-feed/timeline?shipmentId=${shipment.id}`,
        );
        setTimelineEvents(response.events);
      } catch (error) {
        console.error("Error fetching timeline:", error);
        setTimelineEvents([]);
      } finally {
        setTimelineLoading(false);
      }
    };
    fetchTimeline();
  }, [shipment]);

  // Timeline event click → open email dialog
  const handleTimelineEmailClick = useCallback(async (emailId: string) => {
    try {
      const email = await apiClient.get<DemoEmail>(
        `/demo-shipment-feed/email/${emailId}`,
      );
      setEmailDialog(email);
    } catch (error) {
      console.error("Error fetching email:", error);
    }
  }, []);

  // Chat citation click → scroll to and highlight email in timeline
  const handleChatCitationClick = useCallback((messageId: string) => {
    // Switch to Timeline tab and highlight the email
    setDrawerTab(0);
    // Clear first to allow re-highlighting the same email
    setHighlightedEmailId(null);
    setTimeout(() => setHighlightedEmailId(messageId), 50);
  }, []);

  const fetchRemindersForShipment = useCallback(async () => {
    if (!shipment) return { data: [] as Reminder[] };
    return { data: allReminders.filter((r) => r.shipmentId === shipment.id && r.status === "pending") };
  }, [shipment, allReminders]);

  const reminderCount = shipment
    ? allReminders.filter((r) => r.shipmentId === shipment.id && r.status === "pending").length
    : 0;

  return (
    <OutlookPluginProvider context="web" adapter={mockAdapter} api={mockApi} apiClient={mockApiClient}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        variant="persistent"
        PaperProps={{
          sx: { width: DRAWER_WIDTH, borderLeft: "1px solid #e0e0e0" },
        }}
      >
        {shipment && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100vh",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1,
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {shipment.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getPropertyValue(shipment, "port_of_loading")} →{" "}
                  {getPropertyValue(shipment, "port_of_discharge")}
                </Typography>
              </Box>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Tabs - black background, white text */}
            <Tabs
              value={drawerTab}
              onChange={(_e, v) => setDrawerTab(v)}
              sx={{
                minHeight: 40,
                flexShrink: 0,
                backgroundColor: "#1a1a1a",
                "& .MuiTab-root": {
                  color: "rgba(255,255,255,0.7)",
                  minHeight: 40,
                  "&.Mui-selected": { color: "#fedf33" },
                },
                "& .MuiTabs-indicator": { backgroundColor: "#fedf33" },
              }}
            >
              <Tab label="Timeline" />
              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <span>Reminders</span>
                    {reminderCount > 0 && (
                      <Chip
                        icon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
                        label={reminderCount}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          backgroundColor: "#ffa726",
                          color: "#fff",
                          "& .MuiChip-label": { px: 0.75 },
                          "& .MuiChip-icon": { color: "#fff", ml: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>

            {/* Collapsible Shipment Details */}
            <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}>
              <Box
                onClick={() => setDetailsOpen(!detailsOpen)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  bgcolor: "grey.50",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                  Shipment Details
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: "text.secondary",
                    transition: "transform 0.2s",
                    transform: detailsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>
              <Collapse in={detailsOpen}>
                <Box sx={{ px: 1, pb: 1, "& .MuiTypography-colorPrimary": { color: "#1976d2" } }}>
                  <ShipmentDetails
                    shipment={{ id: shipment.id, properties: shipment.properties } as any}
                    maxVisibleDetails={4}
                  />
                </Box>
              </Collapse>
            </Box>

            {/* Tab content - fills remaining space */}
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              {drawerTab === 0 && (
                <>
                  <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}>
                    <Box
                      onClick={() => setTimelineOpen(!timelineOpen)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 0.75,
                        cursor: "pointer",
                        bgcolor: "grey.50",
                        "&:hover": { bgcolor: "grey.100" },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
                        Shipment Timeline
                      </Typography>
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 20,
                          color: "text.secondary",
                          transition: "transform 0.2s",
                          transform: timelineOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      />
                    </Box>
                  </Box>
                  {timelineOpen && (
                    <EventsTimeline
                      events={timelineEvents}
                      loading={timelineLoading}
                      onOpenEmail={handleTimelineEmailClick}
                      highlightedEmailId={highlightedEmailId}
                      numberOfLanes={4}
                      laneThickness={8}
                      showFilters
                      filtersCollapsible
                    />
                  )}
                </>
              )}
              {drawerTab === 1 && (
                <RemindersListComponent
                  fetchReminders={fetchRemindersForShipment}
                  key={shipment.id}
                  hideDraftButton
                />
              )}
            </Box>

            {/* Chat - always pinned at bottom */}
            {defaultChatAnswer && (
              <Box
                sx={{
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "background.paper",
                  flexShrink: 0,
                }}
              >
                <DobbyChat
                  conversationType="shipment_question"
                  extraParams={{ feedId: shipment.id }}
                  mockMode
                  defaultChatAnswer={defaultChatAnswer}
                  onOpenEmail={(messageId) => handleChatCitationClick(messageId)}
                />
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      {/* Email dialog - styled like an email client */}
      <Dialog
        open={emailDialog !== null}
        onClose={() => setEmailDialog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {emailDialog && (
          <Box>
            {/* Email header bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.5,
                backgroundColor: "#f5f5f5",
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontSize: "1rem", fontWeight: 600, flex: 1, mr: 2 }}
              >
                {emailDialog.subject}
              </Typography>
              <IconButton size="small" onClick={() => setEmailDialog(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {/* Sender info */}
              <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: "0.8rem",
                      backgroundColor: "#1976d2",
                      mt: 0.25,
                    }}
                  >
                    {emailDialog.from?.[0]
                      ? getInitials(emailDialog.from[0])
                      : <PersonIcon fontSize="small" />}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {emailDialog.from?.join(", ")}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap", ml: 2 }}
                      >
                        {emailDialog.date ? formatEmailDate(emailDialog.date) : ""}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      To: {emailDialog.to?.join(", ")}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Email body */}
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.7,
                    color: "text.primary",
                  }}
                >
                  {emailDialog.body}
                </Typography>
              </Box>

              {/* Attachments */}
              {emailDialog.attachments && emailDialog.attachments.length > 0 && (
                <>
                  <Divider />
                  <Box sx={{ px: 2.5, py: 1.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600, mb: 0.75, display: "block" }}
                    >
                      {emailDialog.attachments.length} Attachment{emailDialog.attachments.length > 1 ? "s" : ""}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {emailDialog.attachments.map((att) => (
                        <Chip
                          key={att.attachmentId}
                          icon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                          label={att.filename}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            borderColor: "#d0d0d0",
                            "& .MuiChip-label": { fontSize: "0.8rem" },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </DialogContent>
          </Box>
        )}
      </Dialog>
    </OutlookPluginProvider>
  );
}
